import type { Permission } from "@/features/roles/constants/permissions";
import type { OrgRole, WorkspaceRole } from "@/types/user";

export const CONTACT_VISIBILITY = {
  ALL: "all",
  TEAMS: "teams",
  ASSIGNED: "assigned",
} as const;

export type ContactVisibility =
  (typeof CONTACT_VISIBILITY)[keyof typeof CONTACT_VISIBILITY];

// A workspace role. `base` keeps the old three-way split for the things that
// still key on it (digests, notification defaults, Echo's handover pool).
export type Role = {
  id: string;
  name: string;
  description: string | null;
  base: WorkspaceRole;
  contactVisibility: ContactVisibility;
  permissions: Permission[];
  isSystem: boolean;
  memberCount: number;
};

// What `roles/catalog` sends: the rules the backend enforces on a role's keys.
export type PermissionCatalog = {
  permissions: Array<{ key: Permission; requires: Permission[] }>;
  workspaceWide: Permission[];
  withheldFromAgents: Permission[];
  presets: Record<WorkspaceRole, Permission[]>;
};

// What decides which contacts a role sees.
export type RoleScope = Pick<Role, "base" | "contactVisibility">;

// What a role gives whoever holds it.
export type RoleGrant = RoleScope & Pick<Role, "permissions">;

export type CreateRoleInput = Pick<
  Role,
  "name" | "description" | "base" | "contactVisibility" | "permissions"
>;

// No role changes its base, and Member and Agent keep their name and description.
export type UpdateRoleInput = Pick<Role, "contactVisibility" | "permissions"> &
  Partial<Pick<Role, "name" | "description">>;

// Why the server sent `access-changed`.
export const ACCESS_CHANGE_REASON = {
  ROLE: "role",
  ROLE_EDITED: "role-edited",
  TEAMS: "teams",
  REMOVED: "removed",
  ORGANIZATION_ROLE: "organization-role",
} as const;

export type AccessChangeReason =
  (typeof ACCESS_CHANGE_REASON)[keyof typeof ACCESS_CHANGE_REASON];

// What `/users/me` says the signed-in user can do in the active workspace.
// Null when they aren't a member of it.
export type Access = {
  roleId: string | null;
  roleName: string | null;
  base: WorkspaceRole | null;
  organizationRole: OrgRole | null;
  permissions: Permission[];
  contactVisibility: ContactVisibility | null;
  teamIds: string[];
};

// Who is looking, as far as the contact wall goes.
export type ContactViewer = {
  userId: string | null;
  contactVisibility: ContactVisibility;
  teamIds: string[];
};
